<template>
  <div class="select-brand" ref="selectBrandRef">
    <div class="head">
      <p>{{ $t('seller.dashboard.selectBrandTitle') }}</p>
      <span>{{ $t('seller.dashboard.selectBrandHint') }}</span>
    </div>
    <div class="table-cont">
      <artmisads-table
        :columns="columns"
        :row-selection="{
          selectedRowKeys: props.selectedRowKeys,
          onChange: props.onSelectChange,
        }"
        row-key="id"
        :data-source="props.brandList"
        :scroll="{ y: 264 }"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.name === 'allProducts'">
            <div v-if="text && props.selectedRowKeys?.find(i => i === record.id)" class="circle-wrap">
              <span class="active-circle">
                <svg class="icon" aria-hidden="true">
                  <use xlink:href="#icon-duigou-jiacu"></use>
                </svg>
              </span>
            </div>
            <div class="circle-wrap" v-else  @click="() => props.changeBrandList(record.id, 'allProducts')">
              <span class="circle"></span>
            </div>
          </template>
          <template v-if="column.name === 'productsLater'">
            <div v-if="text && props.selectedRowKeys?.find(i => i === record.id)" class="circle-wrap">
              <span class="active-circle">
                <svg class="icon" aria-hidden="true">
                  <use xlink:href="#icon-duigou-jiacu"></use>
                </svg>
              </span>
            </div>
            <div class="circle-wrap" v-else @click="() => props.changeBrandList(record.id, 'productsLater')">
              <span class="circle"></span>
            </div>
          </template>
          <template v-if="column.name === 'brandName'">
            <span class="brand-name">{{text}}</span>
          </template>
          <template v-if="column.name === 'commissionRate'">
            <div class="commission-rate">
              <span>{{ formatCommission(record.commissionRate) }}%</span>
              <a-popover
                trigger="click"
                placement="bottomLeft"
                :open="editingBrandId === record.id"
                :getPopupContainer="() => selectBrandRef"
                @openChange="(open) => onPopoverOpenChange(open, record)"
              >
                <template #title>
                  <div class="commission-popover-title">
                    <span>{{ $t('seller.brandsPage.commissionRate') }}</span>
                    <SvgIcon name="close-cu" class="close-icon" @click.stop="closeEdit" />
                  </div>
                </template>
                <template #content>
                  <div class="commission-popover-content">
                    <artmisads-input-number
                      :min="0"
                      :max="100"
                      suffix="%"
                      :step="0.5"
                      size="large"
                      :placeholder="$t('seller.common.pleaseEnter')"
                      v-model:value="editingCommission"
                      @blur="commissionBlur"
                    />
                    <div class="commission-hint">
                      <Icon name="info-tianchong" class="hint-icon" />
                      <span>{{ $t('seller.dashboard.commissionAppliesToBrand') }}</span>
                    </div>
                    <div class="commission-actions">
                      <artmisads-button @click="closeEdit">{{ $t('seller.common.cancel') }}</artmisads-button>
                      <artmisads-button type="primary" @click="saveCommission(record.id)">{{ $t('seller.common.save') }}</artmisads-button>
                    </div>
                  </div>
                </template>
                <SvgIcon name="bianji" class="edit-icon" />
              </a-popover>
            </div>
          </template>
        </template>
      </artmisads-table>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SvgIcon from '~/components/SvgIcon.vue'
import Icon from '~/components/Icon.vue'

const props = defineProps(['brandList', 'onSelectChange', 'selectedRowKeys', 'changeBrandList']);
const { t, locale } = useI18n()

const selectBrandRef = ref(null)
const editingBrandId = ref(null)
const editingCommission = ref(null)

const formatCommission = (value) => {
  const num = Number(value)
  if (Number.isNaN(num)) return 0
  return Number.isInteger(num) ? num : Number(num.toFixed(1))
}

const openEdit = (record) => {
  editingBrandId.value = record.id
  editingCommission.value = record.commissionRate ?? 0
}

const closeEdit = () => {
  editingBrandId.value = null
  editingCommission.value = null
}

const onPopoverOpenChange = (open, record) => {
  if (open) {
    openEdit(record)
  } else {
    closeEdit()
  }
}

const commissionBlur = (e) => {
  const newValue = e.target.value
    .replace(/[^0-9.]/g, '')
    .split('.')
    .map((v, i) => {
      v = v ? parseInt(v) : null
      if (i && v) {
        v = 5
      }
      return v
    })
    .join('.')
  let data = Number(newValue)
  data = data > 100 ? 100 : data
  editingCommission.value = Number.isNaN(data) ? 0 : data
}

const saveCommission = (id) => {
  const value = editingCommission.value === null || editingCommission.value === undefined || editingCommission.value === ''
    ? 0
    : Number(editingCommission.value)
  props.changeBrandList(id, 'commissionRate', Number.isNaN(value) ? 0 : value)
  closeEdit()
}

const columns = computed(() => {
  locale.value
  return [
    {
      title: t('seller.common.brand'),
      dataIndex: 'amazonBrandName',
      name: 'brandName',
      width: '28%',
    },
    {
      title: t('seller.brandsPage.commissionRate'),
      dataIndex: 'commissionRate',
      name: 'commissionRate',
      width: '22%',
    },
    {
      title: t('seller.dashboard.colActivateAllProducts'),
      dataIndex: 'allProducts',
      name: 'allProducts',
      width: '25%',
    },
    {
      title: t('seller.dashboard.colActivateProductsLater'),
      dataIndex: 'productsLater',
      name: 'productsLater',
      width: '25%',
    },
  ]
})

</script>

<style lang="less" scoped>
  .select-brand {
    padding-bottom: 54px;
    .head {
      display: flex;
      > p {
        font-weight: 600;
        font-size: 20px;
        color: var(--dark-1);
        letter-spacing: 0.5px;
        line-height: 28px;
      }
      > span {
        color: var(--dark-3);
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.4px;
        text-align: left;
        margin-left: 8px;
        align-self: center;
      }
    }
    .table-cont {
      margin-top: 16px;
      :deep(.ant-table) {
        .brand-name {
          font-weight: 600;
          font-size: 14px;
          color: var(--dark-1);
          letter-spacing: 0.4px;
          line-height: 19px;
        }
        .commission-rate {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: var(--dark-1);
          letter-spacing: 0.4px;
          line-height: 19px;
          .edit-icon {
            cursor: pointer;
            font-size: 14px;
            color: var(--dark-3);
            &:hover {
              color: var(--primary-1);
            }
          }
        }
        .circle-wrap {
          cursor: pointer;
        }
        .active-circle {
          display: flex;
          justify-content: center;
          width: 16px;
          height: 16px;
          background: #FFFFFF;
          border: 1px solid var(--border-1);
          border-radius: 50%;
          svg {
            width: 12px;
            height: 12px;
            align-self: center;
            fill: var(--green-1);
          }
        }
        .circle {
          display: block;
          width: 16px;
          height: 16px;
          border: 1px solid var(--border-1);
          border-radius: 50%;
        }
      }
    }
    :deep(.ant-popover) {
      .ant-popover-inner {
        border-radius: 8px;
        padding: 0;
      }
      .ant-popover-title {
        padding: 16px 16px 0;
        border-bottom: none;
        min-height: auto;
      }
      .ant-popover-inner-content {
        padding: 12px 16px 16px;
      }
      .commission-popover-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-weight: 600;
        font-size: 16px;
        color: var(--dark-1);
        line-height: 24px;
        .close-icon {
          cursor: pointer;
          font-size: 14px;
          color: var(--dark-3);
        }
      }
      .commission-popover-content {
        width: 320px;
        .commission-hint {
          display: flex;
          align-items: flex-start;
          gap: 4px;
          margin-top: 8px;
          color: var(--dark-3);
          font-size: 12px;
          line-height: 18px;
          .hint-icon {
            width: 14px;
            height: 14px;
            margin-top: 2px;
            flex-shrink: 0;
            fill: var(--dark-3);
          }
        }
        .commission-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 16px;
        }
      }
    }
  }
</style>
